/** @typedef {{ code: string; label: string }} BillingCountryOption */

/** @typedef {{ callingCode: string; minNationalLength: number; maxNationalLength: number; nationalPattern?: RegExp; exampleNational: string }} BillingPhoneRule */

/** @type {Record<string, BillingPhoneRule>} */
export const BILLING_PHONE_RULES = {
  ES: {
    callingCode: '34',
    minNationalLength: 9,
    maxNationalLength: 9,
    nationalPattern: /^[6789]\d{8}$/,
    exampleNational: '612345678',
  },
  PT: {
    callingCode: '351',
    minNationalLength: 9,
    maxNationalLength: 9,
    nationalPattern: /^9\d{8}$/,
    exampleNational: '912345678',
  },
  FR: {
    callingCode: '33',
    minNationalLength: 9,
    maxNationalLength: 9,
    nationalPattern: /^[1-9]\d{8}$/,
    exampleNational: '612345678',
  },
  DE: {
    callingCode: '49',
    minNationalLength: 10,
    maxNationalLength: 11,
    nationalPattern: /^1\d{9,10}$/,
    exampleNational: '15123456789',
  },
  IT: {
    callingCode: '39',
    minNationalLength: 9,
    maxNationalLength: 10,
    nationalPattern: /^3\d{8,9}$/,
    exampleNational: '3123456789',
  },
  GB: {
    callingCode: '44',
    minNationalLength: 10,
    maxNationalLength: 10,
    nationalPattern: /^7\d{9}$/,
    exampleNational: '7123456789',
  },
  US: {
    callingCode: '1',
    minNationalLength: 10,
    maxNationalLength: 10,
    nationalPattern: /^[2-9]\d{9}$/,
    exampleNational: '2025550123',
  },
  MX: {
    callingCode: '52',
    minNationalLength: 10,
    maxNationalLength: 10,
    nationalPattern: /^[1-9]\d{9}$/,
    exampleNational: '5512345678',
  },
  AR: {
    callingCode: '54',
    minNationalLength: 10,
    maxNationalLength: 10,
    nationalPattern: /^9\d{9}$/,
    exampleNational: '9112345678',
  },
  CO: {
    callingCode: '57',
    minNationalLength: 10,
    maxNationalLength: 10,
    nationalPattern: /^3\d{9}$/,
    exampleNational: '3012345678',
  },
};

/** @type {BillingCountryOption[]} */
export const BILLING_COUNTRY_OPTIONS = [
  { code: 'ES', label: 'España (+34)' },
  { code: 'PT', label: 'Portugal (+351)' },
  { code: 'FR', label: 'Francia (+33)' },
  { code: 'DE', label: 'Alemania (+49)' },
  { code: 'IT', label: 'Italia (+39)' },
  { code: 'GB', label: 'Reino Unido (+44)' },
  { code: 'US', label: 'Estados Unidos (+1)' },
  { code: 'MX', label: 'México (+52)' },
  { code: 'AR', label: 'Argentina (+54)' },
  { code: 'CO', label: 'Colombia (+57)' },
];

const DEFAULT_BILLING_COUNTRY = 'ES';

/**
 * @param {string | undefined | null} countryCode
 * @returns {string}
 */
export function resolveBillingCountryCode(countryCode) {
  if (countryCode && Object.hasOwn(BILLING_PHONE_RULES, countryCode)) {
    return countryCode;
  }
  return DEFAULT_BILLING_COUNTRY;
}

/**
 * @param {string} countryCode
 * @returns {BillingPhoneRule}
 */
function getPhoneRule(countryCode) {
  const rule = BILLING_PHONE_RULES[countryCode];
  if (!rule) {
    throw new Error(`Unsupported billing country code: ${countryCode}`);
  }
  return rule;
}

/**
 * @param {string} national
 * @param {BillingPhoneRule} rule
 * @returns {string}
 */
function formatNationalNumber(national, rule) {
  if (!national) {
    return `+${rule.callingCode}`;
  }

  if (rule.callingCode === '34') {
    const chunks = [national.slice(0, 3)];
    for (let index = 3; index < national.length; index += 2) {
      chunks.push(national.slice(index, index + 2));
    }
    return `+${rule.callingCode} ${chunks.join(' ')}`.trim();
  }

  return `+${rule.callingCode} ${national}`;
}

/**
 * @param {string} countryCode
 * @returns {number}
 */
export function getBillingPhoneMaxLength(countryCode) {
  return getBillingPhonePlaceholder(countryCode).length + 2;
}

/**
 * @param {string} countryCode
 * @returns {string}
 */
export function getBillingPhonePlaceholder(countryCode) {
  const rule = getPhoneRule(countryCode);
  return formatNationalNumber(rule.exampleNational, rule);
}

/**
 * @param {string} value
 * @param {string} countryCode
 * @returns {string}
 */
export function formatBillingPhoneInput(value, countryCode) {
  const rule = getPhoneRule(countryCode);
  const trimmed = (value || '').trim();

  if (!trimmed) {
    return '';
  }

  if (trimmed === '+') {
    return '+';
  }

  if (!trimmed.startsWith('+')) {
    return trimmed.replace(/\D/g, '').slice(0, rule.maxNationalLength);
  }

  let digits = `+${trimmed.slice(1).replace(/\D/g, '')}`;
  const numericBody = digits.slice(1);

  if (!numericBody.startsWith(rule.callingCode)) {
    if (numericBody.length <= rule.callingCode.length) {
      return `+${numericBody}`;
    }
    const existingPrefixLen = Math.max(0, numericBody.length - rule.maxNationalLength);
    const national = numericBody.slice(existingPrefixLen, existingPrefixLen + rule.maxNationalLength);
    return formatNationalNumber(national, rule);
  }

  const national = numericBody
    .slice(rule.callingCode.length)
    .slice(0, rule.maxNationalLength);

  return formatNationalNumber(national, rule);
}

/**
 * @typedef {{ valid: true; normalized: string; error: null } | { valid: false; normalized: null; error: string }} BillingPhoneValidation
 */

/**
 * @param {string} value
 * @param {string} countryCode
 * @returns {BillingPhoneValidation}
 */
export function validateBillingPhone(value, countryCode) {
  const trimmed = (value || '').trim();
  const rule = getPhoneRule(countryCode);

  if (!trimmed) {
    return { valid: false, normalized: null, error: 'El teléfono es obligatorio.' };
  }

  if (!trimmed.startsWith('+')) {
    return {
      valid: false,
      normalized: null,
      error: `El teléfono debe incluir el prefijo del país (por ejemplo, +${rule.callingCode}).`,
    };
  }

  const digits = trimmed.replace(/\D/g, '');
  if (!digits.startsWith(rule.callingCode)) {
    return {
      valid: false,
      normalized: null,
      error: `El prefijo debe ser +${rule.callingCode} para el país seleccionado.`,
    };
  }

  const national = digits.slice(rule.callingCode.length);
  if (national.length < rule.minNationalLength || national.length > rule.maxNationalLength) {
    return {
      valid: false,
      normalized: null,
      error: 'Introduce un número de teléfono válido para el país seleccionado.',
    };
  }

  if (rule.nationalPattern && !rule.nationalPattern.test(national)) {
    return {
      valid: false,
      normalized: null,
      error: 'Introduce un número de teléfono válido para el país seleccionado.',
    };
  }

  return {
    valid: true,
    normalized: `+${rule.callingCode}${national}`,
    error: null,
  };
}
